import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Database, Download, Trash2, Play, RefreshCw, CheckCircle2, AlertTriangle, Clock, HardDrive } from 'lucide-react';
import { theme, cardStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  BACKUPS PAGE — list / trigger / download / prune MongoDB backups.
// ═══════════════════════════════════════════════════════════════════

const fmtDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const fmtBytes = (n) => {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1073741824) return `${(n / 1048576).toFixed(2)} MB`;
  return `${(n / 1073741824).toFixed(2)} GB`;
};

const BackupsPage = ({ API_BASE, currentUser }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const isAdmin = ['owner','admin','super_admin'].includes(currentUser?.role);

  const showMsg = (t, text) => { setMsg({ type: t, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API_BASE}/backups`); setList(r.data || []); }
    catch { showMsg('error', 'Load failed'); }
    finally { setLoading(false); }
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const runBackup = async () => {
    if (!window.confirm('Abhi database backup lein? (~1-2 min lagenge)')) return;
    setRunning(true);
    try {
      const r = await axios.post(`${API_BASE}/backups/run`, { triggeredBy: currentUser?.fullName || 'admin' });
      showMsg('success', `✓ ${r.data.history.fileName} (${fmtBytes(r.data.history.sizeBytes)})`);
      load();
    } catch (e) {
      showMsg('error', 'Backup failed: ' + (e.response?.data?.error || e.message));
    } finally { setRunning(false); }
  };

  const del = async (b) => {
    if (!window.confirm(`"${b.fileName}" delete karein?`)) return;
    try { await axios.delete(`${API_BASE}/backups/${b._id}`); showMsg('success', 'Deleted'); load(); }
    catch { showMsg('error', 'Delete failed'); }
  };

  const prune = async () => {
    if (!window.confirm('30 din se purane sab backups delete karein?')) return;
    try { const r = await axios.post(`${API_BASE}/backups/prune`); showMsg('success', r.data.message); load(); }
    catch { showMsg('error', 'Prune failed'); }
  };

  const download = (b) => window.open(`${API_BASE}/backups/${b._id}/download`, '_blank');

  const verifyOne = async (b) => {
    try {
      const r = await axios.post(`${API_BASE}/backups/${b._id}/verify`);
      showMsg(r.data.ok ? 'success' : 'error', `${r.data.ok ? '✓' : '✗'} ${b.fileName}: ${r.data.reason}`);
      load();
    } catch (e) { showMsg('error', 'Verify failed: ' + (e.response?.data?.error || e.message)); }
  };

  const verifyMonthly = async () => {
    if (!window.confirm('Run monthly verification now? (checks latest + random older backup — may take 1-2 min)')) return;
    try {
      const r = await axios.post(`${API_BASE}/backups/verify-monthly`);
      showMsg(r.data.failed > 0 ? 'error' : 'success',
        `Verified ${r.data.verified}: ${r.data.passed} passed, ${r.data.failed} failed`);
      load();
    } catch (e) { showMsg('error', 'Monthly verification failed'); }
  };

  const okCount = list.filter(b => b.status === 'completed' && b.fileExists).length;
  const totalSize = list.filter(b => b.status === 'completed' && b.fileExists).reduce((s, b) => s + (b.sizeBytes || 0), 0);
  const lastGood = list.find(b => b.status === 'completed' && b.fileExists);

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 16, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}><Database size={22} style={{ verticalAlign: -4, marginRight: 6 }} /> Database Backups</h2>
            <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>
              {okCount} backups · {fmtBytes(totalSize)} total · auto-run daily @ 02:30 IST · 30 days retention
            </p>
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={verifyMonthly} style={headBtn}>Verify Monthly</button>
              <button onClick={prune} style={headBtn}>Prune Old</button>
              <button onClick={runBackup} disabled={running} style={{ ...headBtn, background: 'rgba(255,255,255,0.28)' }}>
                {running ? <RefreshCw size={14} style={{ marginRight: 4, animation: 'spin 1s linear infinite' }} /> : <Play size={14} style={{ marginRight: 4 }} />}
                {running ? 'Running…' : 'Backup Now'}
              </button>
            </div>
          )}
        </div>
      </div>

      {msg.text && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 8,
          background: msg.type === 'error' ? '#fee' : '#efe',
          color: msg.type === 'error' ? theme.danger : theme.success,
          border: `1px solid ${msg.type === 'error' ? theme.danger : theme.success}44`,
          fontWeight: 600, fontSize: 13.5,
        }}>{msg.text}</div>
      )}

      {/* Status card */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 14 }}>
        <StatCard icon={<CheckCircle2 size={18} color="#16a34a" />} label="Last successful" value={lastGood ? fmtDate(lastGood.completedAt) : 'None yet'} />
        <StatCard icon={<HardDrive size={18} color="#2563eb" />} label="Total size on disk" value={fmtBytes(totalSize)} />
        <StatCard icon={<Clock size={18} color="#7c3aed" />} label="Next scheduled" value="Daily 02:30 IST" />
      </div>

      {loading ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 40 }}>Loading…</div>
      ) : list.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 50 }}>
          <Database size={40} color={theme.textSubtle} style={{ marginBottom: 10, opacity: 0.5 }} />
          <p style={{ color: theme.textMuted, fontWeight: 600 }}>Koi backup nahi</p>
          <p style={{ fontSize: 13, color: theme.textSubtle }}>Click "Backup Now" to create your first backup</p>
        </div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: theme.bg, textAlign: 'left' }}>
                <th style={th}>Started</th>
                <th style={th}>Filename</th>
                <th style={th}>Size</th>
                <th style={th}>Trigger</th>
                <th style={th}>Status</th>
                <th style={th}>Duration</th>
                <th style={th}>By</th>
                <th style={{ ...th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map(b => (
                <tr key={b._id} style={{ borderTop: `1px solid ${theme.borderLight}` }}>
                  <td style={td}>{fmtDate(b.startedAt)}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11.5 }}>{b.fileName}</td>
                  <td style={td}>{fmtBytes(b.sizeBytes)}</td>
                  <td style={td}><span style={pill(b.trigger === 'cron' ? '#7c3aed' : '#0891b2')}>{b.trigger}</span></td>
                  <td style={td}>
                    {b.status === 'completed' && b.fileExists && <span style={pill('#16a34a')}><CheckCircle2 size={10} style={{ verticalAlign: -1, marginRight: 3 }} />OK</span>}
                    {b.status === 'completed' && !b.fileExists && <span style={pill('#dc2626')}><AlertTriangle size={10} style={{ verticalAlign: -1, marginRight: 3 }} />Missing</span>}
                    {b.status === 'failed' && <span style={pill('#dc2626')} title={b.errorMessage}><AlertTriangle size={10} style={{ verticalAlign: -1, marginRight: 3 }} />Failed</span>}
                    {b.status === 'running' && <span style={pill('#f59e0b')}>Running…</span>}
                  </td>
                  <td style={td}>{b.durationMs ? `${(b.durationMs / 1000).toFixed(1)}s` : '—'}</td>
                  <td style={td}>{b.triggeredBy}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {b.status === 'completed' && b.fileExists && (
                      <>
                        <button onClick={() => download(b)} style={actBtn}><Download size={11} /> Download</button>
                        <button onClick={() => verifyOne(b)} style={{ ...actBtn, marginLeft: 4 }} title={b.verifiedAt ? `Last verified ${fmtDate(b.verifiedAt)}: ${b.verificationNotes || ''}` : 'Never verified'}>
                          {b.verificationStatus === 'ok' ? '✓' : b.verificationStatus === 'corrupt' ? '✗' : '🔍'} Verify
                        </button>
                      </>
                    )}
                    {isAdmin && (
                      <button onClick={() => del(b)} style={{ ...actBtn, marginLeft: 4, background: theme.danger + '15', color: theme.danger, borderColor: theme.danger + '44' }}><Trash2 size={11} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

const StatCard = ({ icon, label, value }) => (
  <div style={{ ...cardStyle, padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
    <div>{icon}</div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: theme.navy }}>{value}</div>
    </div>
  </div>
);

const headBtn = { ...btnStyle, marginTop: 0, background: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' };
const th = { padding: '10px 12px', fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 };
const td = { padding: '10px 12px', color: theme.navy, verticalAlign: 'middle' };
const actBtn = { display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 9px', borderRadius: 5, border: `1px solid ${theme.primary}33`, background: theme.primary + '12', color: theme.primary, cursor: 'pointer', fontSize: 11, fontWeight: 600 };
const pill = (color) => ({ fontSize: 11, fontWeight: 700, color, background: color + '18', padding: '2px 7px', borderRadius: 4, textTransform: 'uppercase' });

export default BackupsPage;
